import {
	useState,
	useEffect,
	useSyncExternalStore,
	useDeferredValue,
	startTransition,
} from 'react';

import { expVal } from '@confluence/feature-experiments';
import {
	EDIT_PAGE_V2,
	EDIT_BLOG_V2,
	EDIT_PAGE,
	EDIT_BLOG,
	EDIT_PAGE_EMBED,
} from '@confluence/named-routes/entry-points/routes.ts';

import { getSSRRenderInputs } from './ssrRenderInputs.ts';

const emptySubscribe = () => () => {};
const getClientSnapshot = () => false;
const getServerSnapshot = () => true;

const editRoutes = [EDIT_PAGE_V2, EDIT_BLOG_V2, EDIT_PAGE, EDIT_BLOG, EDIT_PAGE_EMBED];

const isEditOrLiveRoute = () => {
	const { uri, isLivePage } = getSSRRenderInputs();
	if (isLivePage) {
		return true;
	}
	if (uri) {
		return editRoutes.some((route) => route.match(uri));
	}
	return false;
};

// This hook is used to determine if we should continue to render the server placeholder
// during hydration on the first render to prevent mismatch. If mismatch occurs, hydration
// can break.
export const useRenderServerPlaceholder = (renderServerPlaceholderOnNonHydrate = false): any => {
	const isSSRSync = useSyncExternalStore(emptySubscribe, getClientSnapshot, getServerSnapshot);
	// Confluence uses flushSync to force synchronous hydration on Suspense boundaries
	// useDeferredValue prevents useSyncExternalStore from triggering a conflicting synchronous update (see: https://kurtextrem.de/posts/react-uses-hydration)
	const optimizedRenderServerPlaceholder = useDeferredValue(isSSRSync);

	const isTransition = window.__SSR_RENDERED__ === false;
	const [renderServerPlaceholder, setRenderServerPlaceholder] = useState(
		renderServerPlaceholderOnNonHydrate
			? // eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				!isTransition || Boolean(process.env.REACT_SSR)
			: // eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				((window as any)?.__HYDRATABLE__ && !isTransition) || Boolean(process.env.REACT_SSR),
	);

	useEffect(() => {
		if (
			// TODO: Fix optimization issues with edit and live pages
			// Skip optimization for now since hydration is handled differently
			isEditOrLiveRoute() ||
			!expVal('confluence_optimize_render_server_placeholder', 'isEnabled', false)
		) {
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (!process.env.REACT_SSR) {
				// Clearing the placeholder synchronously can land on a Suspense boundary that has
				// not finished hydrating, which makes React discard the server HTML for that
				// boundary and client-render it instead.
				if (expVal('confluence_frontend_hydration_safe_updates', 'isEnabled', false)) {
					startTransition(() => {
						setRenderServerPlaceholder(false);
					});
				} else {
					setRenderServerPlaceholder(false);
				}
			}
		}
	}, []);

	return !isEditOrLiveRoute() &&
		expVal('confluence_optimize_render_server_placeholder', 'isEnabled', false)
		? optimizedRenderServerPlaceholder
		: renderServerPlaceholder;
};
